<template>
    <UForm
        :schema="schema"
        :state="state"
        @submit="onSubmit"
        class="max-w-md mx-auto p-6 space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md"
    >
        <h2 class="text-2xl font-semibold mb-6 text-center">{{ $t('Login to your account') }}</h2>

        <UAlert v-if="error" close @update:open="error = false" variant="soft" color="error" :title="$t('Login failed')" :description="$t('Please check your email and password and try again.')" class="mb-4" />

        <div class="space-y-4">
            <UFormField :label="$t('Email')" name="email">
                <UInput  class="w-full" size="lg" v-model="state.email" type="email" :placeholder="$t('Enter your email')" />
            </UFormField>

            <UFormField :label="$t('Password')" name="password">
                <UInput class="w-full" size="lg" v-model="state.password" type="password" :placeholder="$t('Enter your password')" />
            </UFormField>
        </div>

        <div class="flex justify-between">
            <UButton :loading="customerStore.pending" :disabled="customerStore.pending" loading-icon="i-lucide-loader" type="submit" color="primary" variant="solid" :label="$t('Login')" size="lg" />
            <ULink :href="$localePath({ name: 'forgot-password' })" class="text-sm text-blue-600 hover:underline self-center">{{ $t('Forgot password?') }}</ULink>
        </div>
        <USeparator :label="$t('or')" class="my-4" />
        <div class="flex justify-center">
            <UButton type="button" color="secondary" variant="outline" :label="$t('Login with Google')" size="lg" />
        </div>
        <USeparator :label="$t('Don\'t have an account?')" class="my-4" />
        <div class="text-center">
            <UButton :href="$localePath({ name: 'register' })" variant="outline" color="primary">{{ $t('Create an account') }}</UButton>
        </div>
    </UForm>
</template>
<script setup lang="ts">
const { t: $t } = useI18n()

import type { FormSubmitEvent } from '@nuxt/ui'
import { z } from 'zod'

const {
    email
} = defineProps<{
    email?: string
}>()

const emit = defineEmits<{
    (e: 'login-success'): void
}>()

const schema = z.object({
    email: z.string().email($t('Invalid email address')),
    password: z.string().min(1, $t('Password is required'))
})

type SchemaOut = z.output<typeof schema>

const state = reactive<SchemaOut>({
    email: email || '',
    password: ''
})

const customerStore = useCustomerStore()
const toast = useToast()
const error = ref<boolean>(false)

watch(state, () => {
    error.value = false
})

const onSubmit = async ({ data } : FormSubmitEvent<SchemaOut>) => {
    // Simulate login API call

    try {
        await customerStore.login(data.email, data.password);
        toast.add({
            title: 'Login success',
            description: 'You have been logged in successfully.',
            color: 'success',
        });
        emit('login-success');
    } catch (e) {
        console.error('Login failed:', e);
        error.value = true
    }
}
</script>